///---------------------------------------------------------------------------
//! Copyright (C) ASQUARED SRL - All Rights Reserved
//* Unauthorized copying of this file, via any medium is strictly prohibited
//* Proprietary and confidential
//* Written by Alexandru Gârbacea <g99.alex@yahoo.com>, September 2022
//? @author g99.alex@yahoo.com
///---------------------------------------------------------------------------

//-----------------------------------------------------------------------------------------
//* \brief
//? Repairs an existing INTRASTAT XML in place.
//?
//? The document tree is edited node by node rather than rebuilt: the file already has the
//? right structure, only some values are wrong. That keeps this small and means a repaired
//? file differs from the original exactly where it had to.
//?
//? Every correction comes from IntrastatRulesService, the same rules the generator and the
//? table use, so the repairer can never drift from them. Anything that cannot be corrected
//? without guessing (an unknown NC8 code, a missing quantity, a partner VAT with no country
//? prefix) is deliberately left alone and reported for the user to resolve.
//-----------------------------------------------------------------------------------------

import {
    normalizeCn8,
    normalizeCountry,
    normalizeDeliveryTerms,
    normalizeTransactionNature,
    normalizeTransportMode,
    toXmlInteger,
    getRequiredSupplUnit,
    toDeclarantVatNr,
    asString,
} from './IntrastatRulesService'
import { validateIntrastatXml } from './XmlValidationService'

const NS = 'http://www.intrastat.ro/xml/InsSchema'

const childOf = (parent, name) => parent.getElementsByTagNameNS(NS, name)[0] || null

//-----------------------------------------------------------------------------------------
//* \brief
//? Applies a normalizer to one element and records the change.
//-----------------------------------------------------------------------------------------
const fixValue = (parent, name, normalize, row, label, repairs) => {
    const node = childOf(parent, name)
    if (!node) return null

    const before = asString(node.textContent).trim()
    const after = asString(normalize(before))
    if (after === before || after === '') return before

    node.textContent = after
    repairs.push({ row, label, message: `"${before}" → "${after}"` })
    return after
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Brings an item's <InsSupplUnitsInfo> in line with what the CN8 code requires:
//? removes it when the code needs no unit, creates it when the code needs one, and
//? corrects the unit code otherwise.
//?
//? Creating it matters for the manual picker: choosing a code that requires a unit would
//? otherwise leave an error the user has no control to resolve. With the block in place
//? the remaining problem becomes a missing quantity, which does have an input.
//? The new element goes after CountryOfOrigin, the position the schema sequence requires.
//-----------------------------------------------------------------------------------------
const reconcileSupplUnits = (item, cn8, repairs, row) => {
    const required = getRequiredSupplUnit(cn8)
    if (required === null) return

    const suppl = childOf(item, 'InsSupplUnitsInfo')

    if (required === '') {
        if (suppl) {
            item.removeChild(suppl)
            repairs.push({
                row, label: 'UM suplimentară',
                message: `eliminată: codul NC8 ${cn8} nu are unitate suplimentară`,
            })
        }
        return
    }

    if (!suppl) {
        const doc = item.ownerDocument
        const block = doc.createElementNS(NS, 'InsSupplUnitsInfo')
        const unit = doc.createElementNS(NS, 'SupplUnitCode')
        unit.textContent = required
        const qty = doc.createElementNS(NS, 'QtyInSupplUnits')
        qty.textContent = '0'
        block.appendChild(unit)
        block.appendChild(qty)

        const origin = childOf(item, 'CountryOfOrigin')
        if (origin && origin.nextSibling) item.insertBefore(block, origin.nextSibling)
        else item.appendChild(block)

        repairs.push({
            row, label: 'UM suplimentară',
            message: `codul NC8 ${cn8} cere "${required}" — completați cantitatea`,
        })
        return
    }

    const unitNode = childOf(suppl, 'SupplUnitCode')
    if (unitNode && asString(unitNode.textContent).trim() !== required) {
        repairs.push({
            row, label: 'UM suplimentară',
            message: `"${unitNode.textContent}" → "${required}" (conform codului NC8 ${cn8})`,
        })
        unitNode.textContent = required
    }
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Repairs one declaration item.
//-----------------------------------------------------------------------------------------
const repairItem = (item, repairs, useNomenclature = true) => {
    const row = item.getAttribute('OrderNr') || '?'

    const cn8 = fixValue(item, 'Cn8Code', (v) => normalizeCn8(v).value, row, 'Cod NC8', repairs)

    fixValue(item, 'InvoiceValue', toXmlInteger, row, 'Valoare fiscală', repairs)
    fixValue(item, 'StatisticalValue', toXmlInteger, row, 'Valoare statistică', repairs)
    fixValue(item, 'NetMass', toXmlInteger, row, 'Greutate netă', repairs)
    fixValue(item, 'DeliveryTermsCode', (v) => normalizeDeliveryTerms(v).value, row, 'Condiție de livrare', repairs)
    fixValue(item, 'ModeOfTransportCode', (v) => normalizeTransportMode(v).value, row, 'Mod de transport', repairs)
    fixValue(item, 'CountryOfOrigin', (v) => normalizeCountry(v).value, row, 'Țara de origine', repairs)
    fixValue(item, 'CountryOfConsignment', (v) => normalizeCountry(v).value, row, 'Țara de expediere', repairs)
    fixValue(item, 'CountryOfDestination', (v) => normalizeCountry(v).value, row, 'Țara de destinație', repairs)
    fixValue(item, 'PartnerCountryCode', (v) => normalizeCountry(v).value, row, 'Țara codului TVA', repairs)

    //? Nature of transaction: B carries the full code, A is its first digit. An empty A
    //? next to a usable B is the single most common defect in files this app produced
    //? before the generator was fixed.
    const bNode = childOf(item, 'NatureOfTransactionBCode')
    const aNode = childOf(item, 'NatureOfTransactionACode')
    if (bNode) {
        const normalized = normalizeTransactionNature(asString(bNode.textContent).trim()).value
        if (normalized !== asString(bNode.textContent).trim() && /^\d\.\d$/.test(normalized)) {
            repairs.push({ row, label: 'Natura tranzacției', message: `"${bNode.textContent}" → "${normalized}"` })
            bNode.textContent = normalized
        }
        const full = asString(bNode.textContent).trim()
        if (aNode && /^\d\.\d$/.test(full) && asString(aNode.textContent).trim() !== full[0]) {
            repairs.push({
                row, label: 'Natura tranzacției',
                message: `codul A completat din codul B "${full}" → "${full[0]}"`,
            })
            aNode.textContent = full[0]
        }
    }

    //? Supplementary units are dictated by the nomenclature, never by the file - which
    //? means they can only be corrected against the matching year. Rewriting a 2025
    //? declaration's units from the 2026 nomenclature would be inventing data.
    if (cn8 && useNomenclature) reconcileSupplUnits(item, cn8, repairs, row)

    const suppl = childOf(item, 'InsSupplUnitsInfo')
    const qtyNode = suppl ? childOf(suppl, 'QtyInSupplUnits') : null
    if (qtyNode) {
        const before = asString(qtyNode.textContent).trim()
        const after = toXmlInteger(before)
        //? a zero quantity is missing data, not a rounding problem - leave it to be
        //? reported so the user supplies the real figure
        if (after !== before && after !== '' && parseInt(after, 10) > 0) {
            qtyNode.textContent = after
            repairs.push({ row, label: 'Cantitate UM suplimentară', message: `"${before}" → "${after}"` })
        }
    }
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Repairs the declaration header.
//-----------------------------------------------------------------------------------------
const repairHeader = (header, repairs) => {
    const fixVat = (parent, label) => {
        const node = childOf(parent, 'VatNr')
        if (!node) return
        const before = asString(node.textContent).trim()
        if (/^[0-9]{10}$/.test(before)) return
        const after = toDeclarantVatNr(before)
        if (after === '' || after === before) return
        node.textContent = after
        repairs.push({ row: 'antet', label, message: `"${before}" → "${after}"` })
    }
    fixVat(header, 'CUI declarant')

    const dtp = childOf(header, 'DTPDetails')
    if (!dtp) return
    fixVat(dtp, 'CUI terț')

    //? LocalityCode and CountyCode are resolved against the locality / county
    //? nomenclatures on import. Present but empty, they resolve to null and kill the
    //? official application with a NullPointerException in
    //? ASUtils.validateDeclarationNomenclatures - removing the empty elements is exactly
    //? what makes such a file importable again.
    const address = childOf(dtp, 'DTPAddress')
    if (!address) return
    ;['Street', 'LocalityCode', 'CountyCode'].forEach((name) => {
        const node = childOf(address, name)
        if (node && asString(node.textContent).trim() === '') {
            address.removeChild(node)
            repairs.push({
                row: 'antet', label: 'Adresă terț',
                message: `<${name}> era gol și a fost eliminat (aplicația oficială respinge fișierul altfel)`,
            })
        }
    })
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Fields the user can resolve by picking a value, mapped to the nomenclature that
//? ModalSetValue already knows how to list. Reusing that component means the picker here
//? searches the exact same data the table uses.
//-----------------------------------------------------------------------------------------
export const PICKABLE_FIELDS = {
    Cn8Code:                  { type: 0,  label: 'Alege codul NC8' },
    DeliveryTermsCode:        { type: 3,  label: 'Alege condiția de livrare' },
    CountryOfOrigin:          { type: 4,  label: 'Alege țara de origine' },
    CountryOfConsignment:     { type: 5,  label: 'Alege țara de expediere' },
    CountryOfDestination:     { type: 5,  label: 'Alege țara de destinație' },
    PartnerCountryCode:       { type: 5,  label: 'Alege țara codului TVA' },
    NatureOfTransactionBCode: { type: 7,  label: 'Alege natura tranzacției' },
    ModeOfTransportCode:      { type: 8,  label: 'Alege modul de transport' },
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Fields the user types a number into rather than picking from a list.
//-----------------------------------------------------------------------------------------
export const NUMERIC_FIELDS = new Set([
    'InvoiceValue', 'StatisticalValue', 'NetMass', 'QtyInSupplUnits',
])

//-----------------------------------------------------------------------------------------
//* \brief
//? Writes one value into one line of the declaration and returns the new XML.
//? `row` is the OrderNr attribute, `field` the element name. Setting the nature of
//? transaction also rewrites its A code, which must always be the first digit of B.
//-----------------------------------------------------------------------------------------
export const setXmlItemValue = (xmlString, row, field, value) => {
    if (typeof DOMParser === 'undefined' || typeof XMLSerializer === 'undefined') return xmlString

    const doc = new DOMParser().parseFromString(xmlString, 'application/xml')
    if (doc.getElementsByTagName('parsererror').length > 0) return xmlString

    const root = doc.documentElement
    const isArrival = root.localName.endsWith('Arrival')
    const items = Array.from(
        root.getElementsByTagNameNS(NS, isArrival ? 'InsArrivalItem' : 'InsDispatchItem')
    )
    const item = items.find((n) => (n.getAttribute('OrderNr') || '') === String(row))
    if (!item) return xmlString

    const node = childOf(item, field)
    if (!node) return xmlString
    node.textContent = asString(value)

    if (field === 'NatureOfTransactionBCode' && /^\d\.\d$/.test(asString(value))) {
        const aNode = childOf(item, 'NatureOfTransactionACode')
        if (aNode) aNode.textContent = asString(value)[0]
    }

    //? A new commodity code brings its own supplementary unit rule with it, so the block
    //? is reconciled straight away instead of leaving the user with an error they have no
    //? control to fix.
    if (field === 'Cn8Code') reconcileSupplUnits(item, asString(value), [], row)

    return new XMLSerializer().serializeToString(doc)
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Repairs a whole declaration.
//? Returns { xml, repairs, result } where result is the validation of the repaired file,
//? so whatever is still listed there needs a human decision.
//-----------------------------------------------------------------------------------------
export const repairIntrastatXml = (xmlString) => {
    const repairs = []

    if (typeof DOMParser === 'undefined' || typeof XMLSerializer === 'undefined') {
        return { xml: xmlString, repairs, result: validateIntrastatXml(xmlString) }
    }

    const doc = new DOMParser().parseFromString(xmlString, 'application/xml')
    if (doc.getElementsByTagName('parsererror').length > 0) {
        return { xml: xmlString, repairs, result: validateIntrastatXml(xmlString) }
    }

    const root = doc.documentElement
    const header = root.getElementsByTagNameNS(NS, 'InsDeclarationHeader')[0]
    if (header) repairHeader(header, repairs)

    //? A declaration written for another CN year cannot be corrected against ours
    const useNomenclature = !validateIntrastatXml(xmlString).cnVersionMismatch

    const isArrival = root.localName.endsWith('Arrival')
    const items = Array.from(
        root.getElementsByTagNameNS(NS, isArrival ? 'InsArrivalItem' : 'InsDispatchItem')
    )
    items.forEach((item) => repairItem(item, repairs, useNomenclature))

    const xml = new XMLSerializer().serializeToString(doc)
    return { xml, repairs, result: validateIntrastatXml(xml) }
}
